import {
    Component,
    Input,
    Output,
    EventEmitter,
    TemplateRef,
    OnInit,
    ViewChild,
    ElementRef
} from '@angular/core';
import { ControlValueAccessor} from '@angular/forms';

import { Observable } from 'rxjs';
import { ToastMessageOptions } from 'primeng/api';
import * as _ from 'underscore';

import { LocalizationService } from '../../../../Core/Localization/services/localization.service';

import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { InputValidationModel } from '../../models/input-validation.model';
import { DropdownComponent } from '../Dropdown/dropdown.component';
import { getProvider } from '../../../../Common/Forms/models/control-value-accessor.model';
import { AgprComponentStyleEnum } from '../../../Basic/enums/social-analysis-types.enum';


@Component({
    selector: 'agpr-dropdown-chips',
    templateUrl: './dropdown.chips.component.html',
    styleUrls: ['./dropdown.chips.component.scss'],
    providers: [getProvider(DropdownChipsComponent)],
    standalone: false
})
export class DropdownChipsComponent implements ControlValueAccessor, OnInit {

    @Input() id: string = null;
    @Input() splitOnPattern: RegExp = null;
    @Input() trimQuotesOnNewChips: boolean = true;
    @Input() disabled: boolean = false;
    @Input() tabindex: number = null;
    @Input() max: number = null;
    @Input() placeholder: string = '';
    @Input() styleClass: string = null;
    @Input() inputStyleClass: string = null;
    @Input() autoCompleteEmptyMessage: string = null;
    @Input() autoCompleteForceSelection: boolean = false;
    @Input() autoCompleteDelay: number = 300;
    @Input() autoCompleteMaxLength: number = null;
    @Input() autoCompleteMinLength: number = 1;
    @Input() autoCompleteScrollHeight: string = '200px';
    @Input() tooltip: string = null;
    @Input() style: AgprComponentStyleEnum = AgprComponentStyleEnum.social;

    // TODO : we could sub in the setter for this if the data could change
    @Input() optionsFetcher: Observable<any[]> = null;
    @Input() labelLookupKey: string = null;
    @Input() labelLookupValue: string = null;

    @Input() validationFunction: Function; // if valid return true
    @Input() chipValidations: InputValidationModel[] = [];
    @Input() errorMessage: string = 'Invalid Input';
    @Input() itemTooltipPosition: string;

    @Input() ariaLabelledBy: string = '';

    @Output() completeMethod: EventEmitter<any> = new EventEmitter<any>();
    @Output() add: EventEmitter<any> = new EventEmitter<any>();
    @Output() remove: EventEmitter<any> = new EventEmitter<any>();
    @Output() focus: EventEmitter<any> = new EventEmitter<any>();
    @Output() blur: EventEmitter<any> = new EventEmitter<any>();
    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() onValidationError: EventEmitter<string> = new EventEmitter<string>();

    @ViewChild(DropdownComponent) theDropdown: DropdownComponent;
    @ViewChild('chipList') chipList: ElementRef;

    options: any[] = null;
    value: any[];
    valueWithLabels: any[] = [];
    newChip: string;
    isAutoComplete: boolean = null;
    showError: boolean;
    chipValidationErrors: ToastMessageOptions[] = [];

    // TODO: Get the token template working the same as in PrimeNG's Chips component
    // @ContentChildren(PrimeTemplate) templates: QueryList<any>;

    public itemTemplate: TemplateRef<any>;

    readonly agprComponentStyle = AgprComponentStyleEnum;

    constructor(
        private localizationService: LocalizationService,
    ) {
        this.value = this.value || [];
    }

    /**
     * Gets the namespace for chip error messages
    */
    public get chipErrorsNamespace(): string {
        const chipErrorsNamespace: string = `chipErrors${this.id}`;

        return chipErrorsNamespace;
    }

    ngOnInit() {
        // Determines if anything is observing the completeMethod output
        this.isAutoComplete = this.completeMethod.observed;
        this.optionsFetcher.subscribe(options => {
            this.options = options;

            if (this.options) {
                this.options.forEach(option => option.label = option[this.labelLookupValue]);
            }

            this.populateModelLabels();
        });
    }

    onModelChange: Function = () => {};

    onModelTouched: Function = () => {};

    /**
     * Required as part of ControlValueAccessor
     */
    registerOnChange(fn: Function): void {
        this.onModelChange = fn;
    }

    /**
     * Required as part of ControlValueAccessor
     */
    registerOnTouched(fn: Function): void {
        this.onModelTouched = fn;
    }

    /**
     * Removes a specified chip from the existing chips
     */
    removeChip(event: Event, chip: any): void {
        let index: number = this.value.indexOf(chip[this.labelLookupKey]);

        event.preventDefault();

        this.value.splice(index, 1);
        this.populateModelLabels();
        this.onModelChange(this.value);

        this.remove.emit({
            originalEvent: event,
            chip
        });
        if (this.value.length === 0) {
            requestAnimationFrame(() => this.theDropdown.focusViewChild.nativeElement.focus());
        }else {
            requestAnimationFrame(() => {
                if (index === this.value.length){
                    index--;
                }
                const buttons = this.chipList.nativeElement.querySelectorAll('button');
                if (buttons[index]) {
                    buttons[index].focus();
                }
            });
        }
    }

    /**
     * Required as part of ControlValueAccessor
     * Adds a new value to the the expression passed to the
     * ngModel property on the component
     */
    writeValue(value: any): void {
        this.value = value;
        this.onModelChange(this.value);
        this.populateModelLabels();
    }

    populateModelLabels() {
        if (this.value && this.options) {
            this.valueWithLabels.length = 0;
            this.value.forEach(model => {
                const option = this.options.find(x => x[this.labelLookupKey] === model);
                if (option) {
                    this.valueWithLabels.push(option);
                }
            });
        }
    }

    /**
     * handler for when the user selects a dropdown option
     */
    public addSelectedChip(event: any): void {

        this.resetChipValidationErrors();

        // Object mode, we need to do duplicate detection a little differently
        let duplicateFound = false;
        for (let i = 0; i < this.value.length; i++) {
            if (this.value[i] === event[this.labelLookupKey]) {
                duplicateFound = true;

                break;
            }
        }
        if (!duplicateFound) {
            if (this.validationFunction) {
                if (!this.validationFunction(event)) {
                    this.theDropdown.updateFocusedOption(null);
                    this.onValidationError.emit(event[this.labelLookupValue]);
                    return;
                };
            }

            this.value.push(event[this.labelLookupKey]);
            this.valueWithLabels.push(event);


            this.add.emit({
                originalEvent: event,
                value: event
            });
        }

        this.theDropdown.updateFocusedOption(null);
        this.onModelChange(this.value);
    }

    /**
     * Determines whether or not the provided
     * chip passes all validation requirements
    */
    public isChipValid(chip: string): boolean {
        const hasChipValidations: boolean = Array.isArray(this.chipValidations);
        let validationResults: boolean[];
        let hasAnyFailedRequirements: boolean;
        let isChipValid: boolean = true;

        if (hasChipValidations) {
            validationResults = this.chipValidations.map(
                validation => this.doesChipPassValidation(chip, validation)
            );

            hasAnyFailedRequirements = _.contains(validationResults, false);
            isChipValid = !hasAnyFailedRequirements;

        }

        return isChipValid;
    }

    /**
     * Validates a chip against a single requirement
    */
    private doesChipPassValidation(chip: string, validation: InputValidationModel): boolean {
        const doesChipPassValidation: boolean = validation.isValid(chip);

        if (!doesChipPassValidation) {
            this.addChipValidationError(validation);
        }

        return doesChipPassValidation;
    }

    /**
     * Adds a chip validation error based on the provided validation test
    */
    private addChipValidationError(validation: InputValidationModel): void {
        const localizedError: string = this.localizationService.transform(validation.errorKey);
        const existingErrorMessage: ToastMessageOptions = _.findWhere(this.chipValidationErrors, {
            detail: localizedError
        });

        const isErrorAlreadyDisplayed: boolean = existingErrorMessage !== null && typeof existingErrorMessage === 'object';

        if (!isErrorAlreadyDisplayed) {
            const errorMessage: ToastMessageOptions = {
                severity: messageSeverityEnum.error,
                detail: localizedError
            };

            this.chipValidationErrors.push(errorMessage);
        }
    }

    /**
     * Resets the chip validation errors
    */
    private resetChipValidationErrors(): void {
        this.chipValidationErrors.length = 0;
    }
}
